import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ArrowRight } from "lucide-react";
import { useCart } from "@/contexts/CartContext";

const SHIPPING = 7.95;
const FREE_POSTAGE_THRESHOLD = 100;

export default function Cart() {
  const { items, updateQty, removeItem, subtotal, count } = useCart();
  const navigate = useNavigate();
  const freePostage = subtotal >= FREE_POSTAGE_THRESHOLD;
  const effectiveShipping = freePostage || items.length === 0 ? 0 : SHIPPING;

  return (
    <div data-testid="cart-page" className="min-h-screen">
      <section className="border-b-[3px] border-black">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
          <div className="font-mono-ui text-xs text-[#ff1493]">— YOUR CART</div>
          <h1 className="font-display text-5xl md:text-6xl leading-none mt-3">Cart ({count})</h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 py-10 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {items.length === 0 ? (
            <div className="border-[3px] border-black p-10 text-center">
              <div className="font-display text-2xl">Your cart is empty.</div>
              <Link to="/shop" className="font-mono-ui text-xs underline mt-3 inline-block">Browse the shop</Link>
            </div>
          ) : (
            items.map((it) => (
              <div
                key={it.product_id}
                data-testid={`cart-item-${it.product_id}`}
                className="border-[3px] border-black bg-white p-4 shadow-brut-sm flex items-center gap-4"
              >
                <img src={it.image_url} alt={it.name} className="w-20 h-20 object-cover border-[3px] border-black" />
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg leading-none truncate">{it.name}</div>
                  <div className="font-mono-ui text-[10px] text-black/50 mt-1">{it.sku}</div>
                  <div className="font-display text-sm mt-2">£{it.price.toFixed(2)}</div>
                </div>
                <div className="inline-flex items-center border-[3px] border-black">
                  <button onClick={() => updateQty(it.product_id, it.quantity - 1)} className="w-9 h-9 flex items-center justify-center hover:bg-[#ff1493] hover:text-white">
                    <Minus className="w-4 h-4" />
                  </button>
                  <div className="w-10 text-center font-mono-ui font-bold">{it.quantity}</div>
                  <button onClick={() => updateQty(it.product_id, it.quantity + 1)} className="w-9 h-9 flex items-center justify-center hover:bg-[#ff1493] hover:text-white">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <div className="hidden sm:block font-display text-xl w-24 text-right">
                  £{(it.price * it.quantity).toFixed(2)}
                </div>
                <button
                  data-testid={`remove-item-${it.product_id}`}
                  onClick={() => removeItem(it.product_id)}
                  className="w-9 h-9 border-[3px] border-black flex items-center justify-center hover:bg-black hover:text-white"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))
          )}
        </div>

        <aside className="h-fit border-[3px] border-black bg-white p-6 shadow-brut">
          <div className="font-display text-2xl mb-4">Summary</div>
          {items.length > 0 && !freePostage && (
            <div className="mb-4 border-[3px] border-black bg-[#facc15] p-3 font-mono-ui text-[10px] leading-relaxed">
              <div className="font-bold">SPEND £{(FREE_POSTAGE_THRESHOLD - subtotal).toFixed(2)} MORE FOR FREE UK POSTAGE</div>
              <div className="mt-2 h-2 bg-white border-[2px] border-black overflow-hidden">
                <div className="h-full bg-[#ff1493]" style={{ width: `${Math.min(100, (subtotal / FREE_POSTAGE_THRESHOLD) * 100).toFixed(0)}%` }} />
              </div>
            </div>
          )}
          {items.length > 0 && freePostage && (
            <div className="mb-4 border-[3px] border-black bg-[#ff1493] text-white p-3 font-mono-ui text-[10px] font-bold">
              ✨ FREE UK POSTAGE UNLOCKED
            </div>
          )}
          <div className="space-y-2 font-mono-ui text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span>£{subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between">
              <span>UK Shipping (or free collection)</span>
              <span>{freePostage && items.length > 0 ? "FREE" : `£${SHIPPING.toFixed(2)}*`}</span>
            </div>
            <div className="border-t-[3px] border-black mt-3 pt-3 flex justify-between font-display text-2xl">
              <span>Total</span>
              <span className="text-[#ff1493]">£{(subtotal + effectiveShipping).toFixed(2)}</span>
            </div>
          </div>
          <div className="mt-2 font-mono-ui text-[10px] text-black/60">*Select FREE in-person collection on checkout to drop shipping to £0. Postage is free on orders over £{FREE_POSTAGE_THRESHOLD}.</div>
          <button
            data-testid="cart-checkout-btn"
            disabled={items.length === 0}
            onClick={() => navigate("/checkout")}
            className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#ff1493] text-white border-[3px] border-black shadow-brut btn-press font-mono-ui text-xs font-bold disabled:opacity-50"
          >
            PROCEED TO CHECKOUT <ArrowRight className="w-4 h-4" />
          </button>
          <div className="mt-4 border-[3px] border-black bg-[#facc15] p-3 font-mono-ui text-[10px] leading-relaxed">
            FOR RESEARCH USE ONLY. YOU WILL CONFIRM ON CHECKOUT THAT YOU'RE A QUALIFIED RESEARCHER.
          </div>
        </aside>
      </section>
    </div>
  );
}
